import { useEffect } from 'react';
import { Outlet, useMatches } from 'react-router';
import { useMe } from '@/features/auth/session-context';
import { useBranchRecovery } from '@/features/branch/use-branch-recovery';
import { useRememberPage } from '@/features/shell/last-page';
import { LARGER_TEXT_SCALE } from '@/features/shell/config';
import { ForbiddenPage } from '@/pages/ForbiddenPage';
import { OfflineBanner, TrainingBanner } from './shell/Banners';
import { IdleLock } from './shell/IdleLock';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';
import styles from './AppShell.module.css';

export interface RouteHandle {
  /** Page title for the topbar; the dashboard shows a greeting instead. */
  title?: string;
}

/** "Larger text": the whole admin 15 % bigger for this person (#215). */
function useLargerText(on: boolean) {
  useEffect(() => {
    const root = document.documentElement;
    root.dataset.largerText = on ? 'true' : 'false';
    root.style.setProperty('zoom', on ? String(LARGER_TEXT_SCALE) : '');
    return () => {
      delete root.dataset.largerText;
      root.style.removeProperty('zoom');
    };
  }, [on]);
}

/**
 * Desktop-first admin frame: sidebar + sticky topbar + routed content, with
 * the offline and training-copy banners and the idle lock (RL-720).
 */
export function AppShell() {
  const matches = useMatches();
  const me = useMe();
  useLargerText(me.largerText);
  // RL-1820: "Report a problem" in Help names the page the user came from.
  useRememberPage();
  // RL-1176: a refused X-Branch-Id reloads `me` and resets the branch.
  const { noBranchAccess } = useBranchRecovery();
  const handle = matches[matches.length - 1]?.handle as RouteHandle | undefined;
  return (
    <IdleLock>
      <div className={styles.shell}>
        <a href="#main" className={styles.skip}>
          Skip to content
        </a>
        <Sidebar />
        <div className={styles.main}>
          <Topbar title={handle?.title} />
          <TrainingBanner />
          <OfflineBanner />
          <main id="main" tabIndex={-1} className={styles.content}>
            {noBranchAccess ? <ForbiddenPage reason="branch" /> : <Outlet />}
          </main>
        </div>
      </div>
    </IdleLock>
  );
}
